import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Mail, Phone, MapPin, User, Tag } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Database } from '../types/database.types';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ActivityTimeline } from '../components/activity/ActivityTimeline';
import { ReminderList } from '../components/reminders/ReminderList';
import { cn } from '../lib/cn';

type Customer = Database['public']['Tables']['customers']['Row'];

export default function CustomerDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [loading, setLoading] = useState(true);
    const [savingStatus, setSavingStatus] = useState(false);

    useEffect(() => {
        if (id) {
            fetchCustomer(id);
        }
    }, [id]);

    const fetchCustomer = async (customerId: string) => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('customers')
                .select('*')
                .eq('id', customerId)
                .single();

            if (error) throw error;
            setCustomer(data);
        } catch (error) {
            console.error('Error fetching customer:', error);
            navigate('/customers'); // Redirect if not found
        } finally {
            setLoading(false);
        }
    };

    // Inattivo si mette a mano; riattivando si ricalcola lo stato dalle fatture (funzione lato database)
    const toggleInactive = async () => {
        if (!customer) return;

        const isInactive = customer.status === 'inactive';
        if (!isInactive && !window.confirm(`Segnare ${customer.company_name} come cliente inattivo?`)) return;

        try {
            setSavingStatus(true);

            if (isInactive) {
                const { data, error } = await supabase.rpc('reactivate_customer', { p_customer_id: customer.id });
                if (error) throw error;
                setCustomer({ ...customer, status: data });
            } else {
                const { error } = await supabase
                    .from('customers')
                    .update({ status: 'inactive' })
                    .eq('id', customer.id);
                if (error) throw error;
                setCustomer({ ...customer, status: 'inactive' });
            }
        } catch (error) {
            console.error('Error updating customer status:', error);
            alert('Non è stato possibile aggiornare lo stato del cliente.');
        } finally {
            setSavingStatus(false);
        }
    };

    if (loading) return <div className="p-8">Caricamento...</div>;
    if (!customer) return <div className="p-8">Cliente non trovato</div>;

    return (
        <div className="mx-auto w-full max-w-7xl py-2 md:py-6 animate-in fade-in duration-500">
            <Button variant="ghost" className="mb-4 pl-0 hover:pl-2 transition-all" onClick={() => navigate('/customers')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Torna alla lista
            </Button>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
                {/* Left Column: Customer Info */}
                <div className="space-y-4 md:space-y-6">
                    <Card className="p-4 md:p-6 border-t-4 border-t-purple-600 shadow-lg">
                        <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-1 break-words">{customer.company_name}</h1>
                        <div className="text-sm text-gray-500 mb-4 flex flex-wrap items-center gap-2">
                            {customer.codice_cliente && <span className="bg-gray-100 px-2 py-0.5 rounded text-xs font-mono">{customer.codice_cliente}</span>}
                            {/* Un cliente attivo non ha etichetta: la si mostra solo quando c'è qualcosa da segnalare */}
                            {customer.status !== 'active' && (
                                <span className={cn(
                                    "px-2 py-0.5 rounded-full text-xs font-medium uppercase",
                                    customer.status === 'inactive' ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
                                )}>
                                    {customer.status === 'inactive' ? 'inattivo' : 'prospect'}
                                </span>
                            )}
                        </div>

                        <div className="space-y-3 text-sm">
                            {customer.contact_person && (
                                <div className="flex items-center gap-3 text-gray-700">
                                    <User className="w-4 h-4 text-gray-400" />
                                    <span>{customer.contact_person}</span>
                                </div>
                            )}
                            {customer.email && (
                                <div className="flex items-center gap-3 text-gray-700 min-w-0">
                                    <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    <a href={`mailto:${customer.email}`} className="hover:underline text-purple-600 break-all">{customer.email}</a>
                                </div>
                            )}
                            {customer.phone && (
                                <div className="flex items-center gap-3 text-gray-700">
                                    <Phone className="w-4 h-4 text-gray-400" />
                                    <a href={`tel:${customer.phone}`} className="hover:underline">{customer.phone}</a>
                                </div>
                            )}
                            {customer.address && (
                                <div className="flex items-start gap-3 text-gray-700">
                                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                                    <span>
                                        {customer.address}<br />
                                        {customer.city} {customer.province} {customer.postal_code}<br />
                                        {customer.country}
                                    </span>
                                </div>
                            )}
                            {customer.vat_number && (
                                <div className="flex items-center gap-3 text-gray-700 pt-2 border-t mt-2">
                                    <Tag className="w-4 h-4 text-gray-400" />
                                    <span className="text-xs text-gray-500">P.IVA: {customer.vat_number}</span>
                                </div>
                            )}
                        </div>

                        <div className="mt-6 pt-4 border-t">
                            <div className="text-xs text-gray-400">Note interne</div>
                            <div className="text-sm text-gray-600 mt-1 italic">
                                {customer.notes || "Nessuna nota presente."}
                            </div>
                        </div>

                        <div className="mt-4 pt-4 border-t">
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full"
                                disabled={savingStatus}
                                onClick={toggleInactive}
                            >
                                {customer.status === 'inactive' ? 'Riattiva cliente' : 'Segna come inattivo'}
                            </Button>
                            <p className="text-xs text-gray-400 mt-2">
                                {customer.status === 'inactive'
                                    ? 'Lo stato verrà ricalcolato dalle fatture emesse.'
                                    : 'Senza fatture negli ultimi 12 mesi diventa inattivo da solo.'}
                            </p>
                        </div>
                    </Card>

                    {/* Stats or other modules can go here */}
                </div>

                {/* Right Column: Activity & Reminders */}
                <div className="lg:col-span-2 space-y-4 md:space-y-6">
                    <ReminderList clientId={customer.id} />

                    <div className="bg-white rounded-lg p-3 md:p-6 shadow-sm border">
                        <ActivityTimeline clientId={customer.id} />
                    </div>
                </div>
            </div>
        </div>
    );
}
